import { invariantResponse } from '@epic-web/invariant'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, NavLink, Outlet, useLoaderData } from '@remix-run/react'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

export async function loader({ params }: LoaderFunctionArgs) {
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			image: { select: { id: true } },
			characters: {
				select: {
					id: true,
					name: true,
					shard: {
						select: {
							name: true,
							server: { select: { name: true } },
						},
					},
				},
				orderBy: { createdAt: 'desc' },
			},
		},
		where: { username: params.username },
	})

	invariantResponse(owner, 'Owner not found', { status: 404 })

	return json({ owner })
}

export default function CharactersRoute() {
	const { owner } = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const isOwner = user?.id === owner.id
	const ownerDisplayName = owner.name ?? owner.username

	return (
		<div className="container mx-auto mb-24 px-4 pt-8 md:mb-48">
			{/* Header */}
			<div className="flex items-center gap-3">
				<Link to={`/users/${owner.username}`}>
					<img
						src={getUserImgSrc(owner.image?.id)}
						alt={ownerDisplayName}
						className="h-10 w-10 rounded-full object-cover md:h-12 md:w-12"
					/>
				</Link>
				<div>
					<Link to={`/users/${owner.username}`} className="text-sm text-muted-foreground hover:underline">
						&larr; {ownerDisplayName}
					</Link>
					<h1 className="text-xl font-bold md:text-2xl">Characters ({owner.characters.length})</h1>
				</div>
				{isOwner && (
					<NavLink
						to="new"
						className="ml-auto rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
					>
						<Icon name="plus" className="mr-1 inline" />
						Add Character
					</NavLink>
				)}
			</div>

			{/* Characters nav + content */}
			<div className="mt-6 flex flex-col gap-6 lg:flex-row">
				{/* Character list */}
				<div className="shrink-0 lg:w-64">
					<div className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-x-visible lg:pb-0">
						{owner.characters.length === 0 && (
							<p className="text-sm text-muted-foreground">No characters yet.</p>
						)}
						{owner.characters.map((char) => (
							<NavLink
								key={char.id}
								to={char.id}
								preventScrollReset
								className={({ isActive }) =>
									`shrink-0 rounded-lg border px-3 py-2 text-sm transition-colors ${
										isActive
											? 'border-primary bg-primary/10 text-primary'
											: 'border-border bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
									}`
								}
							>
								<div className="line-clamp-1 font-medium">{char.name}</div>
								{char.shard && (
									<div className="mt-0.5 flex items-center gap-1 text-xs opacity-60">
										{char.shard.server?.[0]?.name && (
											<>
												<span>{char.shard.server[0].name}</span>
												<span className="opacity-40">·</span>
											</>
										)}
										<span>{char.shard.name}</span>
									</div>
								)}
							</NavLink>
						))}
					</div>
				</div>

				{/* Character content */}
				<div className="min-w-0 flex-1 rounded-xl border bg-muted/30 p-4 md:p-6">
					<Outlet />
				</div>
			</div>
		</div>
	)
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => <p>No user with the username "{params.username}" exists</p>,
			}}
		/>
	)
}
